import React, { useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { PlusIcon } from "@heroicons/react/16/solid";
import { Input } from "../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { useUser } from "../../../contexts/UserContext";
import { Checkbox, CheckboxField } from "../../../design/checkbox";
import { User } from "../../../models/User";
import { UserType } from "../../../models/UserType";
import { Select } from "../../../design/select";

interface Props {
  user: User;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  type?: string;
}

const EditUserButton: React.FC<Props> = ({ user }) => {
  const { createUser } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState<string>(user.firstName ?? "");
  const [lastName, setLastName] = useState<string>(user.lastName ?? "");
  const [email, setEmail] = useState<string>(user.email);
  const [type, setType] = useState<UserType>(user.type);

  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingMore, setIsCreatingMore] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!firstName.trim()) {
      newErrors.firstName = "First name is required.";
    }
    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required.";
    }
    if (!email.trim()) {
      newErrors.email = "Email is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      await createUser({
        firstName,
        lastName,
        email,
        role: "user",
        phoneNumber: "",
        isOnboarded: false,
        type,
      });
      clearForm();
    } catch (error) {
      console.error("Error creating location:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    if (!isCreatingMore) {
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        <PlusIcon className="w-5 h-5" aria-hidden="true" />
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>First Name</Label>
                  <Input
                    type="text"
                    value={firstName}
                    placeholder="First Name"
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Last Name</Label>
                  <Input
                    type="text"
                    value={lastName}
                    placeholder="Last Name"
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </Field>
              </div>

              <Field>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  placeholder="Email"
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Type</Label>
                <Select
                  value={type}
                  onChange={(e) => setType(e.target.value as UserType)}
                >
                  <option value={""} disabled>
                    Select a type
                  </option>
                  <option value={"user"}>User</option>
                  <option value={"professional"}>Professional</option>
                </Select>
                {errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions className="justify-between">
          <CheckboxField>
            <Checkbox
              checked={isCreatingMore}
              onChange={(checked) => setIsCreatingMore(checked)}
            />
            <Label>Edit more</Label>
          </CheckboxField>
          <div className="flex gap-2">
            <Button plain onClick={clearForm}>
              Cancel
            </Button>
            <Button disabled={isSaving} onClick={handleSubmit}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditUserButton;
